import {
  ActionPropertyDescriptionFragment,
  ActionPropertyType,
  ActionPropertyValueFragment,
  DisplayRule,
  PropertyMatchOperator,
} from '$/GraphQL/schema/types';

import { DropdownItemProps } from 'semantic-ui-react';
import {
  HasNoResponseOperator,
  HasResponseOperator,
  MatchOperator,
  PropertyMatchOperatorFactory,
} from './MatchOperator';

type ActionPropertyValueType = undefined | null | string | string[] | boolean | number;
export type ActionPropertiesValueHash = Record<string, ActionPropertyValue>;
interface ActionPropertyValueSource {
  actionPropertiesValueHash?: ActionPropertiesValueHash;
}

type DropdownOptionProps = Omit<DropdownItemProps, 'description'>;

export class ActionPropertyValue {
  constructor(
    public readonly prop: ActionPropertyDescriptionFragment | ActionPropertyValueFragment
  ) {}

  public static defaultOperators = [new HasResponseOperator(), new HasNoResponseOperator()];

  public propertyValue(source: ActionPropertyValueSource): ActionPropertyValueType {
    const { actionPropertiesValueHash } = source;
    if (
      !this.prop ||
      !actionPropertiesValueHash ||
      !(this.prop.property in actionPropertiesValueHash)
    )
      return undefined;
    return actionPropertiesValueHash[this.prop.property]?.value;
  }

  public performDisplayRule(source: ActionPropertyValueSource, displayRule: DisplayRule): boolean {
    if (!displayRule.property) return false;
    const propertyValue = this.propertyValue(source);
    return PropertyMatchOperatorFactory.instanceOf(displayRule.operator).perform(
      displayRule,
      propertyValue
    );
  }

  public operators(): MatchOperator[] {
    switch (this.prop.type) {
      case ActionPropertyType.Selection:
      case ActionPropertyType.MultiSelection:
      case ActionPropertyType.Countries:
      case ActionPropertyType.ExpandableSelection:
      case ActionPropertyType.ExpandableMultiSelection:
        return [
          PropertyMatchOperator.HasNoResponse,
          PropertyMatchOperator.HasResponse,
          PropertyMatchOperator.IsAnyOf,
          PropertyMatchOperator.IsNoneOf,
        ].map((o) => PropertyMatchOperatorFactory.instanceOf(o));
      case ActionPropertyType.Text:
      case ActionPropertyType.Hidden:
      case ActionPropertyType.States:
      case ActionPropertyType.MultilineText:
      case ActionPropertyType.Email:
      case ActionPropertyType.Phone:
      case ActionPropertyType.Number:
        return [
          PropertyMatchOperator.HasNoResponse,
          PropertyMatchOperator.HasResponse,
          PropertyMatchOperator.Contains,
          PropertyMatchOperator.NotContain,
          PropertyMatchOperator.StartsWith,
          PropertyMatchOperator.EndsWith,
          PropertyMatchOperator.IsExactly,
        ].map((o) => PropertyMatchOperatorFactory.instanceOf(o));
      case ActionPropertyType.Url:
        return [
          PropertyMatchOperator.Contains,
          PropertyMatchOperator.NotContain,
          PropertyMatchOperator.StartsWith,
          PropertyMatchOperator.EndsWith,
          PropertyMatchOperator.IsExactly,
        ].map((o) => PropertyMatchOperatorFactory.instanceOf(o));

      case ActionPropertyType.DateTime:
        return [
          PropertyMatchOperator.HasNoResponse,
          PropertyMatchOperator.HasResponse,
          PropertyMatchOperator.IsExactly,
        ].map((o) => PropertyMatchOperatorFactory.instanceOf(o));

      case ActionPropertyType.Boolean:
        return [
          new HasResponseOperator(this.prop.values?.[0]),
          new HasNoResponseOperator(this.prop.values?.[1]),
        ];
    }
  }

  public defaultOperator(): MatchOperator {
    return this.operators()[0];
  }

  public isSelectionType(): boolean {
    return [
      ActionPropertyType.Selection,
      ActionPropertyType.MultiSelection,
      ActionPropertyType.Countries,
      ActionPropertyType.ExpandableSelection,
      ActionPropertyType.ExpandableMultiSelection,
    ].includes(this.prop.type);
  }

  public isTextType(): boolean {
    return [
      ActionPropertyType.Text,
      ActionPropertyType.Hidden,
      ActionPropertyType.States,
      ActionPropertyType.Url,
      ActionPropertyType.MultilineText,
      ActionPropertyType.Email,
      ActionPropertyType.Phone,
      ActionPropertyType.Number,
    ].includes(this.prop.type);
  }

  public getDropdownOptions(): DropdownOptionProps[] | undefined {
    if (!this.isSelectionType()) return undefined;

    // built-in values
    if (this.prop.values?.length) {
      return this.prop.values?.map((v) => ({ value: v, text: v }));
    }

    return undefined;
  }

  public get allowAdditions(): boolean {
    return [
      ActionPropertyType.ExpandableSelection,
      ActionPropertyType.ExpandableMultiSelection,
    ].includes(this.prop.type);
  }

  public get value(): ActionPropertyValueType {
    return (this.prop as ActionPropertyValueFragment).value;
  }
}
